import { useMemo } from "react";
import {
  QUERY_CITY_LIST,
  QUERY_CREATE_CITY,
  QUERY_PAGINATED_CITIES,
  QUERY_PUBLISH_MAP,
  QUERY_REMOVE_CITY,
  QUERY_UPDATE_CITY,
} from "services/web/api-cities.const";
import { useTrackedMutation, useTrackedQuery } from "@/hooks/apollo-insights-hooks";

import { FilterParams } from "types";

export interface PaginatedCitiesProps {
  cityName: string;
  currentPage: FilterParams;
  setCurrentPage: React.Dispatch<React.SetStateAction<FilterParams>>;
}

export const usePaginatedCitiesActions = ({ cityName, currentPage, setCurrentPage }: PaginatedCitiesProps) => {
  const variables = useMemo(() => {
    return {
      pageIndex: currentPage.pageIndex,
      pageSize: currentPage.pageSize,
      sortField: currentPage.sortField,
      sortOrder: currentPage.sortOrder,
      filter: [{ field: "name", value: cityName }],
    };
  }, [cityName, currentPage.pageIndex, currentPage.pageSize, currentPage.sortField, currentPage.sortOrder]);

  const refetchQueriesConfig = useMemo(() => {
    return [{ query: QUERY_PAGINATED_CITIES, variables }, { query: QUERY_CITY_LIST }];
  }, [variables]);

  const {
    data: cities,
    loading,
    refetch,
  } = useTrackedQuery(QUERY_PAGINATED_CITIES, {
    variables: variables,
    actionName: "QueryPaginatedCities",
  });

  const [deleteCity] = useTrackedMutation(QUERY_REMOVE_CITY, {
    refetchQueries: refetchQueriesConfig,
    actionName: "RemoveCityMutation",
    awaitRefetchQueries: true,
    onCompleted: async () => {
      const currentPageIndex =
        currentPage.pageIndex !== 1 && cities?.paginatedCities.results.length === 1
          ? currentPage.pageIndex - 1
          : currentPage.pageIndex;
      if (currentPageIndex !== currentPage.pageIndex) {
        setCurrentPage({ ...currentPage, pageIndex: currentPageIndex });
      }
    },
  });

  const [createNewCity] = useTrackedMutation(QUERY_CREATE_CITY, {
    refetchQueries: refetchQueriesConfig,
    actionName: "AddCityMutation",
    awaitRefetchQueries: true,
  });

  const [updateCity] = useTrackedMutation(QUERY_UPDATE_CITY, {
    refetchQueries: refetchQueriesConfig,
    actionName: "UpdateCityMutation",
    awaitRefetchQueries: true,
  });

  return {
    deleteCity,
    createNewCity,
    cities,
    refetch,
    loading,
    updateCity,
  };
};

export const usePublishMap = () => {
  const [publishMap, { data, loading }] = useTrackedMutation(QUERY_PUBLISH_MAP, {
    actionName: "QueryPublishMap",
  });
  return {
    publishMap,
    data,
    loading,
  };
};
